import { Chessboard as ReactChessboard } from "react-chessboard"
import DashboardTopbar from "@/components/DashboardTopbar"
import { useGame } from "@/hooks/useGame"
import BoardControls from "./BoardControls"
import BoardBackground from "./BoardBackground"
import Annotations from "./Annotations"
import Analysis from "./Analysis"
import ActionBar from "./ActionBar"
import ImportButton from "./ImportButton"
import GuestActionBar from "@/components/guest/GuestActionBar"
import MovesList from "./MovesList"
import { useGameMoves } from "@/hooks/useGameMoves"
import ScrollProvider from "../ScrollProvider"

type GuestActions = {
  onSave: () => void
  onClear: () => void
}

const ChessScreen = ({
  isOwner,
  guestActions,
}: {
  isOwner: boolean
  guestActions?: GuestActions
}) => {
  const {
    game,
    movesHistory,
    activeMove,
    isDraggablePiece,
    onPieceDrop,
    onPieceDragBegin,
    onSquareClick,
    optionSquares,
  } = useGameMoves()
  const { title, orientation, initialGame } = useGame()

  const hasContent = movesHistory.length > 0 && (
    Array.isArray(movesHistory[0]) ? movesHistory[0].length > 0 : false
  )

  return (
    <ScrollProvider dependency={activeMove}>
      {(movesContainerRef) => (
        <>
          <DashboardTopbar
            title={guestActions ? "Create your first ChessDrop game!" : title}
            badge={initialGame.chess960 ? "Freestyle" : undefined}
            backHref={guestActions ? "/" : "/games"}
          >
            {isOwner ? (
              <ActionBar />
            ) : guestActions ? (
              <GuestActionBar
                onSave={guestActions.onSave}
                onClear={guestActions.onClear}
                hasContent={hasContent}
              />
            ) : (
              <ImportButton />
            )}
          </DashboardTopbar>

          <main className="relative z-10 flex flex-1 flex-col min-[1500px]:pr-[500px] lg:pr-[335px] xl:pr-96">
            <BoardBackground>
              <ReactChessboard
                boardOrientation={orientation}
                position={game.fen()}
                areArrowsAllowed={false}
                onSquareClick={onSquareClick}
                autoPromoteToQueen={false}
                onPromotionCheck={() => false}
                customDarkSquareStyle={{
                  backgroundColor: "#c56c1a",
                }}
                customLightSquareStyle={{
                  backgroundColor: "#ffd17f",
                }}
                onPieceDragBegin={(_piece, sourceSquare) =>
                  onPieceDragBegin(sourceSquare)
                }
                isDraggablePiece={({ piece }) => isDraggablePiece(piece)}
                onPieceDrop={(sourceSquare, targetSquare) =>
                  onPieceDrop(sourceSquare, targetSquare)
                }
                customSquareStyles={optionSquares}
              />
            </BoardBackground>

            <div className="flex grow flex-col-reverse overflow-y-auto border-t border-gray-200 bg-white min-[1500px]:w-[500px] lg:fixed lg:top-18 lg:right-0 lg:bottom-0 lg:z-50 lg:flex lg:w-[335px] lg:flex-col lg:border-t-0 lg:border-l xl:w-96">
              <div className="pb-safe flex h-1/2 flex-col border-t border-gray-200 bg-gray-50 lg:border-t-0 lg:border-b lg:pb-0">
                <div
                  ref={movesContainerRef}
                  className="flex min-h-[100px] shrink-0 flex-col items-start justify-start space-y-1.5 overflow-auto scroll-smooth p-6"
                >
                  <MovesList depth={0} moves={movesHistory} />
                </div>
                <Annotations isOwner={isOwner} />
              </div>

              <div className="px-6 pt-4">
                <Analysis />
                <BoardControls />
              </div>
            </div>
          </main>
        </>
      )}
    </ScrollProvider>
  )
}

export default ChessScreen
